import { useMemo, useState, type FormEvent } from 'react';
import { buildSchedule, FINANCING_KINDS, FINANCING_KIND_LABELS, FINANCING_OBJECT_KINDS, FINANCING_OBJECT_LABELS, formatCents, type CreateFinancingInput, type FinancingDetail, type FinancingKind } from '@kontor/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { parseBp, parseCents } from '../../money';
import { useSession } from '../../session';
import { formatDay, percentText } from './shared';
import '../fleet/fleet.css';
import './financing.css';

const centsText = (c: number | null | undefined) => (c ? (c / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());

/**
 * Vertrag anlegen oder ändern (KON-253). Die Eingabefelder hängen von der Art ab: Leasing braucht die monatliche Rate (brutto) und kennt eine Sonderzahlung und eine Kilometerbegrenzung, Darlehen und
 * Ratenkauf brauchen Betrag und Zinssatz oder Rate, dazu eine Schlussrate. Unter dem Formular zeigt eine Vorschau, was Wirkia daraus rechnet, bevor gespeichert wird.
 */
export function FinancingForm({ financing, onSubmit, onCancel, pending, error }: { financing?: FinancingDetail; onSubmit: (body: CreateFinancingInput) => void; onCancel: () => void; pending: boolean; error: string | null }) {
  const { hasModule, can } = useSession();
  const f = financing;
  const [kind, setKind] = useState<FinancingKind>(f?.kind ?? 'lease');
  const [title, setTitle] = useState(f?.title ?? '');
  const [lender, setLender] = useState(f?.lenderName ?? '');
  const [contractNumber, setContractNumber] = useState(f?.contractNumber ?? '');
  const [objectKind, setObjectKind] = useState(f?.objectKind ?? 'vehicle');
  const [vehicleId, setVehicleId] = useState(f?.vehicleId ?? '');
  const [objectDescription, setObjectDescription] = useState(f?.objectDescription ?? '');
  const [startDate, setStartDate] = useState(f?.startDate ?? today());
  const [term, setTerm] = useState(String(f?.termMonths ?? 36));
  const [principal, setPrincipal] = useState(centsText(f?.principalCents));
  const [down, setDown] = useState(centsText(f?.downPaymentCents));
  const [rate, setRate] = useState(f?.annualRateBp != null ? (f.annualRateBp / 100).toLocaleString('de-DE') : '');
  // Bei Darlehen bleibt die Rate leer, solange Wirkia sie aus dem Zinssatz rechnet
  const [installment, setInstallment] = useState(f && (f.kind === 'lease' || f.annualRateBp === null) ? centsText(f.installmentCents) : '');
  const [balloon, setBalloon] = useState(centsText(f?.balloonCents));
  const [fees, setFees] = useState(centsText(f?.feesCents));
  const [taxRate, setTaxRate] = useState<0 | 7 | 19>(f?.taxRate ?? 19);
  const [deductible, setDeductible] = useState(f?.inputTaxDeductible ?? true);
  const [directDebit, setDirectDebit] = useState(f?.directDebit ?? true);
  const [markPast, setMarkPast] = useState(true);
  const [mileage, setMileage] = useState(f?.mileagePerYearKm != null ? String(f.mileagePerYearKm) : '');
  const [excess, setExcess] = useState(f?.excessKmCents != null ? String(f.excessKmCents) : '');
  const [account, setAccount] = useState(f?.ledgerAccount ?? '');
  const [note, setNote] = useState(f?.note ?? '');
  const [local, setLocal] = useState<string | null>(null);

  const vehicles = useQuery({
    queryKey: ['fleet-vehicles-picker'],
    queryFn: async () => (await api<{ data: { id: string; plate: string; name: string | null }[] }>('GET', '/fleet/vehicles?perPage=100')).data,
    enabled: hasModule('fleet') && can('fleet.read'),
  });

  const isLease = kind === 'lease';
  const termMonths = Number(term);
  const parsed = {
    principal: principal.trim() ? parseCents(principal) : null,
    down: down.trim() ? parseCents(down) : 0,
    rate: rate.trim() ? parseBp(rate) : null,
    installment: installment.trim() ? parseCents(installment) : null,
    balloon: balloon.trim() ? parseCents(balloon) : 0,
    fees: fees.trim() ? parseCents(fees) : 0,
  };

  // Vorschau aus denselben Rechenregeln wie auf dem Server
  const preview = useMemo(() => {
    if (!Number.isInteger(termMonths) || termMonths < 1 || [parsed.down, parsed.balloon, parsed.fees].some((v) => v === null)) return null;
    const plan = buildSchedule({ kind, startDate, termMonths, principalCents: isLease ? null : parsed.principal, downPaymentCents: parsed.down ?? 0, annualRateBp: isLease ? null : parsed.rate, installmentCents: parsed.installment, balloonCents: parsed.balloon ?? 0, feesCents: parsed.fees ?? 0, taxRate });
    return plan;
  }, [kind, startDate, termMonths, taxRate, isLease, parsed.principal, parsed.down, parsed.rate, parsed.installment, parsed.balloon, parsed.fees]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setLocal(null);
    if (parsed.down === null || parsed.balloon === null || parsed.fees === null) return setLocal('Bitte prüfen Sie die Beträge, sie müssen Zahlen sein.');
    if (rate.trim() && parsed.rate === null) return setLocal('Bitte geben Sie den Zinssatz als Zahl an, zum Beispiel 4,5.');
    if (principal.trim() && parsed.principal === null) return setLocal('Bitte geben Sie den Betrag als Zahl an, zum Beispiel 35.000,00.');
    if (installment.trim() && parsed.installment === null) return setLocal('Bitte geben Sie die Rate als Zahl an, zum Beispiel 399,00.');
    if (preview?.error) return setLocal(preview.error);
    const body: CreateFinancingInput = {
      kind,
      title: title.trim(),
      lenderName: lender.trim(),
      contractNumber: contractNumber.trim() || null,
      objectKind,
      vehicleId: objectKind === 'vehicle' && vehicleId ? vehicleId : null,
      objectDescription: objectDescription.trim() || null,
      startDate,
      termMonths,
      principalCents: isLease ? null : parsed.principal,
      downPaymentCents: parsed.down ?? 0,
      annualRateBp: isLease ? null : parsed.rate,
      installmentCents: parsed.installment,
      balloonCents: parsed.balloon ?? 0,
      feesCents: parsed.fees ?? 0,
      taxRate: isLease ? taxRate : 0,
      inputTaxDeductible: deductible,
      directDebit,
      mileagePerYearKm: isLease && mileage.trim() ? Number(mileage.replace(/\./g, '')) : null,
      excessKmCents: isLease && excess.trim() ? Number(excess.replace(',', '.')) : null,
      ledgerAccount: account.trim() || null,
      note: note.trim() || null,
    };
    // Nur beim Anlegen: Der Haken „bereits fällige Raten als bezahlt vermerken“ gehört nicht zum Ändern
    onSubmit(f ? body : { ...body, markPastPaid: startDate < today() && !directDebit && markPast });
  };

  const net = isLease && parsed.installment ? Math.round(parsed.installment / (1 + taxRate / 100)) : null;
  const rows = preview?.rows ?? [];
  const interest = rows.reduce((n, r) => n + r.interestCents, 0);
  const lastDate = rows.length > 0 ? rows[rows.length - 1]!.dueDate : null;

  return (
    <form className="card fleet-form" onSubmit={submit} aria-label={f ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}>
      <h2>{f ? `Vertrag ${f.number} bearbeiten` : 'Neuer Vertrag'}</h2>
      {(error || local) && <div className="alert alert--error" role="alert">{local ?? error}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Art</span>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value as FinancingKind)} disabled={!!f}>
            {FINANCING_KINDS.map((k) => <option key={k} value={k}>{FINANCING_KIND_LABELS[k]}</option>)}
          </select>
        </label>
        <label className="field"><span>Bezeichnung</span><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150} required placeholder="zum Beispiel Mercedes E 220 d, Leasing" /></label>
        <label className="field"><span>{isLease ? 'Leasinggeber' : 'Geldgeber (Bank)'}</span><input className="input" value={lender} onChange={(e) => setLender(e.target.value)} maxLength={150} required /></label>
        <label className="field"><span>Vertragsnummer</span><input className="input" value={contractNumber} onChange={(e) => setContractNumber(e.target.value)} maxLength={100} /></label>
        <label className="field">
          <span>Gegenstand</span>
          <select className="input" value={objectKind} onChange={(e) => setObjectKind(e.target.value as typeof objectKind)}>
            {FINANCING_OBJECT_KINDS.map((k) => <option key={k} value={k}>{FINANCING_OBJECT_LABELS[k]}</option>)}
          </select>
        </label>
        {objectKind === 'vehicle' && (vehicles.data?.length ?? 0) > 0 && (
          <label className="field">
            <span>Fahrzeug der Flotte</span>
            <select className="input" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">Nicht zugeordnet</option>
              {vehicles.data!.map((v) => <option key={v.id} value={v.id}>{v.plate}{v.name ? ` · ${v.name}` : ''}</option>)}
            </select>
          </label>
        )}
        <label className="field"><span>Beschreibung des Gegenstands</span><input className="input" value={objectDescription} onChange={(e) => setObjectDescription(e.target.value)} maxLength={200} /></label>
        <label className="field">
          <span>Vertragsbeginn (erste Rate am)</span>
          <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          {startDate < today() && <small className="muted">Bestehender Vertrag: Die Raten seit diesem Tag gelten als bezahlt, bei Lastschrift automatisch.</small>}
        </label>
        <label className="field"><span>Laufzeit in Monaten</span><input className="input" inputMode="numeric" value={term} onChange={(e) => setTerm(e.target.value.replace(/\D/g, ''))} required /></label>
        {!isLease && <label className="field"><span>{kind === 'loan' ? 'Darlehensbetrag (€)' : 'Finanzierter Kaufpreis (€)'}</span><input className="input" inputMode="decimal" value={principal} onChange={(e) => setPrincipal(e.target.value)} required placeholder="35.000,00" /></label>}
        {!isLease && <label className="field"><span>Sollzins pro Jahr (%)</span><input className="input" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="4,5" /></label>}
        <label className="field">
          <span>{isLease ? 'Monatliche Rate brutto (€)' : 'Monatliche Rate (€), leer = aus dem Zinssatz rechnen'}</span>
          <input className="input" inputMode="decimal" value={installment} onChange={(e) => setInstallment(e.target.value)} required={isLease} placeholder={isLease ? '474,81' : ''} />
          {net !== null && <small className="muted">Netto {formatCents(net)} und {formatCents(parsed.installment! - net)} Umsatzsteuer</small>}
        </label>
        {isLease && (
          <label className="field">
            <span>Umsatzsteuer</span>
            <select className="input" value={taxRate} onChange={(e) => setTaxRate(Number(e.target.value) as 0 | 7 | 19)}>
              <option value={19}>19 %</option><option value={7}>7 %</option><option value={0}>0 %</option>
            </select>
          </label>
        )}
        {isLease && <label className="field"><span>Sonderzahlung zu Beginn brutto (€)</span><input className="input" inputMode="decimal" value={down} onChange={(e) => setDown(e.target.value)} /></label>}
        <label className="field"><span>{isLease ? 'Restwert oder Kaufoption (€), zur Information' : 'Schlussrate am Ende (€)'}</span><input className="input" inputMode="decimal" value={balloon} onChange={(e) => setBalloon(e.target.value)} /></label>
        <label className="field"><span>Einmalige Gebühren (€)</span><input className="input" inputMode="decimal" value={fees} onChange={(e) => setFees(e.target.value)} placeholder="Bearbeitung, Zulassung" /></label>
        {isLease && <label className="field"><span>Freikilometer pro Jahr</span><input className="input" inputMode="numeric" value={mileage} onChange={(e) => setMileage(e.target.value.replace(/[^\d.]/g, ''))} /></label>}
        {isLease && <label className="field"><span>Mehrkilometer in Cent je km</span><input className="input" inputMode="numeric" value={excess} onChange={(e) => setExcess(e.target.value.replace(/\D/g, ''))} placeholder="8" /></label>}
        <label className="field"><span>Sachkonto für den Aufwand</span><input className="input" value={account} onChange={(e) => setAccount(e.target.value)} maxLength={20} placeholder={isLease ? 'Leasingaufwand' : 'Zinsaufwand'} /></label>
        <label className="field"><span>Bemerkung</span><input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} /></label>
      </div>
      <label className="check"><input type="checkbox" checked={directDebit} onChange={(e) => setDirectDebit(e.target.checked)} /> Die Raten werden per Lastschrift eingezogen (gelten ab dem Fälligkeitstag als bezahlt, ohne dass Sie sie vermerken)</label>
      {!f && startDate < today() && !directDebit && <label className="check"><input type="checkbox" checked={markPast} onChange={(e) => setMarkPast(e.target.checked)} /> Bereits fällige Raten als bezahlt vermerken (bestehender Vertrag)</label>}
      {!f && startDate < today() && <p className="muted small">Tragen Sie einen laufenden Vertrag mit seinem ursprünglichen Beginn und den Vertragsdaten ein. Kennen Sie nur die aktuelle Restschuld, nehmen Sie sie als Betrag und das Datum der nächsten Rate als Beginn.</p>}
      {isLease && <label className="check"><input type="checkbox" checked={deductible} onChange={(e) => setDeductible(e.target.checked)} /> Vorsteuer ist abziehbar (sonst zählt die Steuer zu den Kosten)</label>}
      {f && <p className="muted small">Ändern Sie Beträge, Zinssatz, Laufzeit oder Beginn, rechnet Wirkia den Zahlungsplan neu. Das geht nur, solange keine Rate von Hand als bezahlt vermerkt ist.</p>}

      {preview && (
        <p className="fin-preview" role="status">
          {preview.error ? preview.error : (
            <>
              {rows.length > 0 && <>Wirkia rechnet: Rate {formatCents(preview.installmentCents)}{!isLease && parsed.rate !== null ? ` bei ${percentText(parsed.rate)} Zinsen` : ''}, {rows.filter((r) => r.kind === 'regular').length} Raten bis {formatDay(lastDate)}
              {!isLease && <>, Zinsen insgesamt {formatCents(interest)}</>}
              {isLease && <>, Raten insgesamt {formatCents(preview.installmentCents * termMonths + (parsed.down ?? 0))} brutto</>}.</>}
            </>
          )}
        </p>
      )}

      <div className="form-actions fin-form-actions">
        <button type="submit" className="btn btn--primary" disabled={pending}>{pending ? 'Wird gespeichert …' : f ? 'Speichern' : 'Vertrag anlegen'}</button>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}
